import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { startMusic, stopMusic } from '@/audio/music';
import { setAmbienceEnabled, setMuted, setVolume } from '@/audio';

const STORAGE_KEY = 'waldermarch.settings';

interface Persisted {
  /**
   * Bumped when a default changes in a way existing players should receive.
   *
   * Saved settings are merged over the defaults, which is right: a choice
   * somebody made should outlive an update. But it also means a corrected
   * default never reaches anyone who has run the game once. The version lets a
   * specific change be pushed through exactly once, without touching the
   * settings they actually chose.
   */
  version: number;
  muted: boolean;
  master: number;
  ui: number;
  game: number;
  voice: number;
  ambience: number;
  ambienceOn: boolean;
  music: number;
  musicOn: boolean;
  confirmDestructive: boolean;
  showBuildingLabels: boolean;
}

const SETTINGS_VERSION = 2;

const DEFAULTS: Persisted = {
  version: SETTINGS_VERSION,
  muted: false,
  master: 0.7,
  ui: 1,
  game: 1,
  voice: 0.85,
  // Ambience is a bed, not a feature: loud enough to notice if it stopped and
  // no louder. It sat at 50%, which put birdsong over the top of the score.
  ambience: 0.05,
  ambienceOn: true,
  music: 0.3,
  musicOn: true,
  confirmDestructive: true,
  showBuildingLabels: true,
};

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    // Merge rather than replace, so a setting added later gets its default
    // instead of arriving as undefined.
    const saved = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Persisted>) };

    // Version 2 corrected the two mix levels. Anyone who played before this
    // has the old values saved and would never see the fix, so they are taken
    // once. Everything else they set is left exactly as it was.
    if ((saved.version ?? 1) < 2) {
      saved.ambience = DEFAULTS.ambience;
      saved.music = DEFAULTS.music;
      saved.version = SETTINGS_VERSION;
    }
    return saved;
  } catch {
    return { ...DEFAULTS };
  }
}

export const useSettingsStore = defineStore('settings', () => {
  const saved = load();

  const muted = ref(saved.muted);
  const master = ref(saved.master);
  const ui = ref(saved.ui);
  const game = ref(saved.game);
  const voice = ref(saved.voice);
  const ambience = ref(saved.ambience);
  const ambienceOn = ref(saved.ambienceOn);
  const music = ref(saved.music);
  const musicOn = ref(saved.musicOn);
  const confirmDestructive = ref(saved.confirmDestructive);
  const showBuildingLabels = ref(saved.showBuildingLabels);

  const open = ref(false);

  function push(): void {
    setMuted(muted.value);
    setVolume('master', master.value);
    setVolume('ui', ui.value);
    setVolume('game', game.value);
    setVolume('voice', voice.value);
    setVolume('ambience', ambience.value);
    setVolume('music', music.value);
    setAmbienceEnabled(ambienceOn.value && !muted.value && ambience.value > 0);
    // The score streams, so it is started and stopped rather than merely
    // faded: an eighteen-minute download should not begin unasked.
    if (musicOn.value && !muted.value && music.value > 0) startMusic();
    else stopMusic();
  }

  function persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: SETTINGS_VERSION,
          muted: muted.value,
          master: master.value,
          ui: ui.value,
          game: game.value,
          voice: voice.value,
          ambience: ambience.value,
          ambienceOn: ambienceOn.value,
          music: music.value,
          musicOn: musicOn.value,
          confirmDestructive: confirmDestructive.value,
          showBuildingLabels: showBuildingLabels.value,
        } satisfies Persisted),
      );
    } catch {
      // Private browsing and full quotas are not worth failing over.
    }
  }

  watch([muted, master, ui, game, voice, ambience, ambienceOn, music, musicOn], () => {
    push();
    persist();
  });
  watch([confirmDestructive, showBuildingLabels], persist);

  // Apply what was loaded before anything plays.
  push();

  function reset(): void {
    muted.value = DEFAULTS.muted;
    master.value = DEFAULTS.master;
    ui.value = DEFAULTS.ui;
    game.value = DEFAULTS.game;
    voice.value = DEFAULTS.voice;
    ambience.value = DEFAULTS.ambience;
    ambienceOn.value = DEFAULTS.ambienceOn;
    confirmDestructive.value = DEFAULTS.confirmDestructive;
    showBuildingLabels.value = DEFAULTS.showBuildingLabels;
  }

  return {
    muted,
    master,
    ui,
    game,
    voice,
    ambience,
    ambienceOn,
    music,
    musicOn,
    confirmDestructive,
    showBuildingLabels,
    open,
    reset,
  };
});
